import { afterSetState, isset } from "legacy/helpers.es6"
import MapMarinaMapCard from "legacy/map/MapMarinaMapCard"
import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import PropTypes from "prop-types"
import React from "react"
import { createRoot } from "react-dom/client"

class MapMarinaMap extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      mapStyle: "streets",
    }

    this.markers = []
    this.mapboxContainerRef = React.createRef()

    // Bind methods to this
    this.mapMoved = this.mapMoved.bind(this)
    this.dataUpdated = this.dataUpdated.bind(this)
    this.easeMapToLocation = this.easeMapToLocation.bind(this)
    this.resizeMap = this.resizeMap.bind(this)
    this.clearOldPins = this.clearOldPins.bind(this)
    this.setMapPins = this.setMapPins.bind(this)
    this.checkboxChange = this.checkboxChange.bind(this)
    this.streets = this.streets.bind(this)
    this.satellite = this.satellite.bind(this)
    this.loadingIndactor = this.loadingIndactor.bind(this)
    this.renderMapCard = this.renderMapCard.bind(this)
    this.createPopupWithReactContent =
      this.createPopupWithReactContent.bind(this)
    this.closeOtherPopups = this.closeOtherPopups.bind(this)
  }

  componentDidMount() {
    mapboxgl.accessToken = process.env.MAPBOX_TOKEN
    this.map = new mapboxgl.Map({
      container: "mapbox-container", // container id
      style: "mapbox://styles/mapbox/streets-v9", // stylesheet location
      center: [this.props.state.currentLon, this.props.state.currentLat], // starting position
      zoom: this.props.state.zoom, // starting zoom
      minZoom: 6, // set the min zoom level
    })
    this.map.on("moveend", this.mapMoved)
    this.map.addControl(new mapboxgl.NavigationControl(), "bottom-right")
  }

  mapMoved(event, updatedFromEaseTo) {
    if (this.props.state.searchOnMove || isset(event.updatedFromEaseTo)) {
      this.props.setParentState({ location: null })
      this.runningid = Math.random()
      // Only reload the map after it has been moved and untouched 200 milliseconds
      setTimeout(
        (runningid) => {
          if (this.runningid === runningid) {
            this.props.mapChange()
          }
        },
        200,
        this.runningid
      )
    }
  }

  dataUpdated() {
    afterSetState(() => {
      this.clearOldPins()
      this.setMapPins()
    })
  }

  easeMapToLocation(lon, lat) {
    this.map.easeTo(
      { center: [lon, lat], zoom: 9 },
      { updatedFromEaseTo: true }
    )
  }

  resizeMap() {
    this.map.resize()
  }

  clearOldPins() {
    this.markers.forEach(function (marker) {
      marker.remove()
    })
    this.markers = []
    this.setState({ activePopup: null })
  }

  closeOtherPopups(marinaPinId) {
    // Close any other open popups when a new one is clicked
    this.markers.forEach(function (marker) {
      if (marker.id !== marinaPinId && marker.getPopup().isOpen()) {
        marker.getPopup().remove()
      }
    })

    this.setState({ activePopup: marinaPinId })
  }

  renderMapCard(lngLat, marina) {
    return (
      <MapMarinaMapCard
        key={marina.id}
        marina={marina}
        experienceName={"map_card"}
        hoverMarina={this.props.hoverMarina}
        blurMarina={this.props.blurMarina}
      />
    )
  }

  createPopupWithReactContent(lngLat, marina) {
    const popupNode = document.createElement("div")
    const root = createRoot(popupNode)
    root.render(this.renderMapCard(lngLat, marina))

    return new mapboxgl.Popup({
      offset: [0, -15],
      maxWidth: "none",
      closeButton: true,
      closeOnMove: false,
    })
      .setLngLat(lngLat)
      .setDOMContent(popupNode)
      .on("open", () => this.closeOtherPopups(marina.id))
  }

  setMapPins() {
    this.props.state.marinas.forEach(
      function (marina) {
        // Create marker element
        const el = document.createElement("div")
        el.className =
          marina.visible_form === "inquiry_form"
            ? "marker inquiry-only"
            : "marker"
        el.id = "marina-marker-" + marina.id
        el.addEventListener(
          "mouseenter",
          this.props.hoverMarina.bind(null, marina.id)
        )
        el.addEventListener("mouseleave", this.props.blurMarina)

        // Create marker with React-rendered popup
        const marker = new mapboxgl.Marker(el, { offset: [0, -17.5] })
          .setLngLat([marina.longitude, marina.latitude])
          .setPopup(
            this.createPopupWithReactContent(
              [marina.longitude, marina.latitude],
              marina
            )
          )
          .addTo(this.map)

        // Store the marina ID with the marker for reference
        marker.id = marina.id

        // Add click handler to marker to close other popups
        el.addEventListener("click", () => {
          this.closeOtherPopups(marina.id)
        })

        this.markers.push(marker)
      }.bind(this)
    )
  }

  checkboxChange(event) {
    this.props.setParentState({ searchOnMove: event.target.checked })
  }

  streets() {
    this.setState({ mapStyle: "streets" })
    this.map.setStyle("mapbox://styles/mapbox/streets-v9")
  }

  satellite() {
    this.setState({ mapStyle: "satellite" })
    this.map.setStyle("mapbox://styles/mapbox/satellite-v9")
  }

  loadingIndactor() {
    if (this.props.state.processing) {
      return (
        <div className="map-loading-indicator">
          <i className="fa-kit fa-spinner fa-spin" />
        </div>
      )
    }
    return null
  }

  render() {
    return (
      <div className="map-marina-map">
        <div className="map-move-form">
          <div className="checkbox">
            <label>
              <input
                type="checkbox"
                checked={this.props.state.searchOnMove}
                onChange={this.checkboxChange}
              />{" "}
              Search as I move the map
            </label>
          </div>
        </div>
        <div className="map-style-toggle">
          <div className="radio">
            <label>
              <input
                type="radio"
                checked={this.state.mapStyle === "streets"}
                onChange={this.streets}
              />{" "}
              Streets
            </label>
          </div>
          <div className="radio">
            <label>
              <input
                type="radio"
                checked={this.state.mapStyle === "satellite"}
                onChange={this.satellite}
              />{" "}
              Satellite
            </label>
          </div>
        </div>
        <button
          className="btn btn-secondary btn-hide-map lg:hidden"
          onClick={this.props.hideMapMobile}
        >
          Hide Map
        </button>
        {this.loadingIndactor()}
        <div
          ref={this.mapboxContainerRef}
          id="mapbox-container"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    )
  }
}

MapMarinaMap.propTypes = {
  state: PropTypes.shape({
    currentLat: PropTypes.string,
    currentLon: PropTypes.string,
    zoom: PropTypes.number,
    marinas: PropTypes.array,
    processing: PropTypes.bool,
    searchOnMove: PropTypes.bool,
  }).isRequired,
  setParentState: PropTypes.func.isRequired,
  mapChange: PropTypes.func.isRequired,
  hoverMarina: PropTypes.func.isRequired,
  blurMarina: PropTypes.func.isRequired,
  hideMapMobile: PropTypes.func.isRequired,
}

export default MapMarinaMap
